import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CloudinaryImage } from '../../types/Gallery';
import './HeroImageEditor.css';

export interface HeroImageSettings {
  position: 'default' | 'center' | 'left' | 'right' | 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'face' | 'custom' | 'crop';
  zoom: number;
  customX?: number; // Percentage 0-100
  customY?: number; // Percentage 0-100
  cropBox?: {
    x: number; // Top-left X position (percentage)
    y: number; // Top-left Y position (percentage)
    width: number; // Width (percentage)
    height: number; // Height (percentage)
  };
}

interface HeroImageEditorProps {
  heroImage: CloudinaryImage | null;
  currentSettings: HeroImageSettings;
  onSettingsChange: (settings: HeroImageSettings) => void;
  targetWidth?: number;
  targetHeight?: number;
}

const HeroImageEditor: React.FC<HeroImageEditorProps> = ({
  heroImage,
  currentSettings,
  onSettingsChange,
  targetWidth = 800,
  targetHeight = 600
}) => {
  const [focusPoint, setFocusPoint] = useState({ x: 50, y: 50 });
  const [zoom, setZoom] = useState(1.0);
  const [previewUrl, setPreviewUrl] = useState('');
  const [cropBox, setCropBox] = useState({
    x: 25, // 25% from left
    y: 25, // 25% from top
    width: 50, // 50% width
    height: 50 // 50% height
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragType, setDragType] = useState<'move' | 'resize' | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize from current settings
  useEffect(() => {
    setZoom(currentSettings.zoom || 1.0);
    
    if (currentSettings.position === 'crop' && currentSettings.cropBox) {
      setCropBox(currentSettings.cropBox);
    } else if (currentSettings.position === 'custom' && currentSettings.customX !== undefined) {
      setFocusPoint({ x: currentSettings.customX, y: currentSettings.customY || 50 });
    } else {
      // Convert preset position to focus point
      const presetPoint = getPresetFocusPoint(currentSettings.position);
      setFocusPoint(presetPoint);
    }
  }, [currentSettings]);

  const getPresetFocusPoint = (position: string): { x: number; y: number } => {
    switch (position) {
      case 'default': return { x: 50, y: 50 }; // Default - no transformations
      case 'center': return { x: 50, y: 50 };
      case 'left': return { x: 0, y: 50 };
      case 'right': return { x: 100, y: 50 };
      case 'top': return { x: 50, y: 0 };
      case 'bottom': return { x: 50, y: 100 };
      case 'top-left': return { x: 0, y: 0 };
      case 'top-right': return { x: 100, y: 0 };
      case 'bottom-left': return { x: 0, y: 100 };
      case 'bottom-right': return { x: 100, y: 100 };
      case 'face': return { x: 50, y: 50 }; // Face detection uses Cloudinary AI
      default: return { x: 50, y: 50 };
    }
  };

  const generateCloudinaryUrl = (imageUrl: string, point?: { x: number; y: number }, zoomLevel?: number, cropBoxData?: { x: number; y: number; width: number; height: number }): string => {
    // Extract publicId from URL
    const uploadIndex = imageUrl.indexOf('/upload/');
    if (uploadIndex === -1) {
      console.log('⚠️ HeroImageEditor: Invalid Cloudinary URL format', { imageUrl });
      return imageUrl;
    }
    
    const publicIdWithPath = imageUrl.substring(uploadIndex + 8);
    console.log('🔧 HeroImageEditor: Generating Cloudinary URL', { 
      publicIdWithPath, 
      point, 
      zoomLevel, 
      cropBoxData,
      targetWidth, 
      targetHeight 
    });
    
    // Import and use the utility function
    const { getHeroImageTransformWithCustomCoords, getHeroImageTransformWithCropBox } = require('../../utils/ImagePositioning');
    
    let transformedUrl: string;
    
    if (cropBoxData && currentSettings.position === 'crop') {
      // Use crop box transformation
      transformedUrl = getHeroImageTransformWithCropBox(
        publicIdWithPath,
        cropBoxData.x,
        cropBoxData.y,
        cropBoxData.width,
        cropBoxData.height,
        targetWidth,
        targetHeight
      );
    } else {
      // Use focal point transformation
      transformedUrl = getHeroImageTransformWithCustomCoords(
        publicIdWithPath,
        point?.x || 50,
        point?.y || 50,
        zoomLevel || 1.0,
        targetWidth,
        targetHeight
      );
    }
    
    console.log('✅ HeroImageEditor: Generated URL', { transformedUrl });
    return transformedUrl;
  };

  // Throttled preview URL update function
  const updatePreviewUrl = useCallback(() => {
    console.log('🎨 HeroImageEditor: Updating preview URL', { 
      focusPoint, 
      zoom, 
      cropBox, 
      position: currentSettings.position,
      heroImage: heroImage?.url 
    });
    if (!heroImage?.url) {
      console.log('⚠️ HeroImageEditor: No hero image URL available');
      return;
    }

    // If position is 'default', use original URL without transformations
    if (currentSettings.position === 'default') {
      console.log('🖼️ HeroImageEditor: Using default (original) image');
      setPreviewUrl(heroImage.url);
      return;
    }

    // Generate Cloudinary URL with positioning and zoom
    let url: string;
    
    if (currentSettings.position === 'crop') {
      console.log('🔧 HeroImageEditor: Using crop mode', { cropBox });
      url = generateCloudinaryUrl(heroImage.url, undefined, undefined, cropBox);
    } else {
      console.log('🔧 HeroImageEditor: Using focal point mode', { focusPoint, zoom });
      url = generateCloudinaryUrl(heroImage.url, focusPoint, zoom);
    }
    
    // Add cache-busting parameter to force browser to fetch new image
    const cacheBuster = Date.now();
    url = url + (url.includes('?') ? '&' : '?') + `cb=${cacheBuster}`;
    
    console.log('🖼️ HeroImageEditor: Generated preview URL', { 
      url, 
      focusPoint, 
      zoom, 
      cropBox, 
      position: currentSettings.position 
    });
    setPreviewUrl(url);
  }, [heroImage, heroImage?.url, focusPoint, zoom, cropBox, currentSettings.position, targetWidth, targetHeight]);

  // Throttled preview update with debouncing
  const throttledUpdatePreview = useCallback(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      updatePreviewUrl();
    }, isDragging ? 100 : 0); // Longer delay during dragging
  }, [updatePreviewUrl, isDragging]);

  // Update preview URL when settings change (with throttling)
  useEffect(() => {
    throttledUpdatePreview();
  }, [throttledUpdatePreview]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Click-to-focus disabled - use crop box for precise positioning
    // Cloudinary doesn't support arbitrary x/y percentage coordinates
    if (!previewRef.current) return;
    
    // If not in crop mode, switch to crop mode on click
    if (currentSettings.position !== 'crop') {
      console.log('💡 Switching to crop mode for precise positioning');
      onSettingsChange({
        position: 'crop',
        zoom: zoom,
        cropBox: cropBox
      });
    }
  };

  // Crop Box Interaction Handlers
  const handleCropBoxMouseDown = (e: React.MouseEvent, type: 'move' | 'resize') => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!previewRef.current) return;
    
    const rect = previewRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setIsDragging(true);
    setDragType(type);
    setDragStart({ x, y });
    
    console.log('🎯 Crop Box: Started drag', { type, x, y });
  };

  const handleCropBoxMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !previewRef.current) return;
    
    const rect = previewRef.current.getBoundingClientRect();
    const currentX = ((e.clientX - rect.left) / rect.width) * 100;
    const currentY = ((e.clientY - rect.top) / rect.height) * 100;
    
    const deltaX = currentX - dragStart.x;
    const deltaY = currentY - dragStart.y;
    
    if (dragType === 'move') {
      // Move the entire crop box
      const newCropBox = {
        x: Math.max(0, Math.min(100 - cropBox.width, cropBox.x + deltaX)),
        y: Math.max(0, Math.min(100 - cropBox.height, cropBox.y + deltaY)),
        width: cropBox.width,
        height: cropBox.height
      };
      setCropBox(newCropBox);
      // Note: Settings will be updated when drag ends to prevent infinite loops
    } else if (dragType === 'resize') {
      // Resize the crop box
      const newCropBox = {
        x: cropBox.x,
        y: cropBox.y,
        width: Math.max(10, Math.min(100 - cropBox.x, cropBox.width + deltaX)),
        height: Math.max(10, Math.min(100 - cropBox.y, cropBox.height + deltaY))
      };
      setCropBox(newCropBox);
      // Note: Settings will be updated when drag ends to prevent infinite loops
    }
    
    setDragStart({ x: currentX, y: currentY });
  };

  const handleCropBoxMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      setDragType(null);
      
      // Update settings with new crop box
      onSettingsChange({
        position: 'crop',
        zoom: zoom,
        cropBox: cropBox
      });
      
      console.log('🎯 Crop Box: Drag ended', { cropBox });
    }
  };


  const handleZoomChange = (newZoom: number) => {
    console.log('🔍 HeroImageEditor: Zoom changed', { newZoom, focusPoint });
    setZoom(newZoom);
    onSettingsChange({
      position: currentSettings.position === 'custom' ? 'custom' : currentSettings.position,
      zoom: newZoom,
      customX: focusPoint.x,
      customY: focusPoint.y
    });
  };

  const handlePresetClick = (position: 'center' | 'left' | 'right' | 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'face') => {
    const point = getPresetFocusPoint(position);
    setFocusPoint(point);
    onSettingsChange({
      position: position,
      zoom: zoom,
      customX: point.x,
      customY: point.y
    });
  };

  const handleCropModeToggle = () => {
    const newPosition = currentSettings.position === 'crop' ? 'center' : 'crop';
    onSettingsChange({
      position: newPosition,
      zoom: zoom,
      cropBox: newPosition === 'crop' ? cropBox : undefined
    });
  };

  const handleReset = () => {
    setFocusPoint({ x: 50, y: 50 });
    setZoom(1.0);
    setCropBox({ x: 25, y: 25, width: 50, height: 50 });
    onSettingsChange({
      position: 'default',
      zoom: 1.0,
      customX: 50,
      customY: 50,
      cropBox: undefined
    });
  };

  if (!heroImage) {
    return (
      <div className="hero-editor-placeholder">
        <i className="fas fa-image"></i>
        <p>Upload an image to configure hero image positioning</p>
      </div>
    );
  }

  return (
    <div className="hero-image-editor">
      <div className="hero-editor-header">
        <h4>
          <i className="fas fa-crop"></i> Hero Image Crop Editor
        </h4>
        {currentSettings.position === 'default' ? (
          <div className="alert alert-info" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '4px' }}>
            <i className="fas fa-info-circle"></i> <strong>Default Mode:</strong> Image is displayed without any positioning or cropping. Click "Enable Crop Mode" to customize.
          </div>
        ) : (
          <p className="hero-editor-help">
            Drag the green crop box to position and resize - use corner handles to adjust size. The preview shows exactly what will appear on your website.
          </p>
        )}
      </div>

      <div className="hero-editor-content">
        {/* Main Preview with Click-to-Focus */}
        <div className="hero-preview-container">
          <div 
            ref={previewRef}
            className="hero-preview-image"
            onClick={handleImageClick}
            onMouseMove={handleCropBoxMouseMove}
            onMouseUp={handleCropBoxMouseUp}
            style={{ 
              cursor: currentSettings.position === 'crop' ? 'default' : 'pointer' 
            }}
            title={currentSettings.position !== 'crop' ? 'Click to enable crop mode for precise positioning' : ''}
          >
            <img 
              ref={imageRef}
              key={`main-preview-${focusPoint.x}-${focusPoint.y}-${zoom}-${cropBox.x}-${cropBox.y}-${cropBox.width}-${cropBox.height}`}
              src={currentSettings.position === 'crop' ? (heroImage.url || heroImage.secure_url) : (previewUrl || heroImage.url || heroImage.secure_url)}
              alt="Hero preview"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            
            
            {/* Crop Box Overlay - only show when actively editing */}
            {currentSettings.position === 'crop' && (
              <div 
                className="crop-box-overlay"
                style={{
                  left: `${cropBox.x}%`,
                  top: `${cropBox.y}%`,
                  width: `${cropBox.width}%`,
                  height: `${cropBox.height}%`
                }}
                onMouseDown={(e) => handleCropBoxMouseDown(e, 'move')}
              >
              {/* Crop box border */}
              <div className="crop-box-border"></div>
              
              {/* Corner resize handles */}
              <div 
                className="crop-handle crop-handle-nw"
                onMouseDown={(e) => handleCropBoxMouseDown(e, 'resize')}
                title="Drag to resize from top-left corner"
              ></div>
              <div 
                className="crop-handle crop-handle-ne"
                onMouseDown={(e) => handleCropBoxMouseDown(e, 'resize')}
                title="Drag to resize from top-right corner"
              ></div>
              <div 
                className="crop-handle crop-handle-sw"
                onMouseDown={(e) => handleCropBoxMouseDown(e, 'resize')}
                title="Drag to resize from bottom-left corner"
              ></div>
              <div 
                className="crop-handle crop-handle-se"
                onMouseDown={(e) => handleCropBoxMouseDown(e, 'resize')}
                title="Drag to resize from bottom-right corner"
              ></div>
              
              {/* Edge resize handles */}
              <div 
                className="crop-handle crop-handle-n"
                onMouseDown={(e) => handleCropBoxMouseDown(e, 'resize')}
                title="Drag to resize from top edge"
              ></div>
              <div 
                className="crop-handle crop-handle-s"
                onMouseDown={(e) => handleCropBoxMouseDown(e, 'resize')}
                title="Drag to resize from bottom edge"
              ></div>
              <div 
                className="crop-handle crop-handle-w"
                onMouseDown={(e) => handleCropBoxMouseDown(e, 'resize')}
                title="Drag to resize from left edge"
              ></div>
              <div 
                className="crop-handle crop-handle-e"
                onMouseDown={(e) => handleCropBoxMouseDown(e, 'resize')}
                title="Drag to resize from right edge"
              ></div>
              
              {/* Center drag handle */}
              <div 
                className="crop-handle crop-handle-center"
                onMouseDown={(e) => handleCropBoxMouseDown(e, 'move')}
                title="Drag to move crop box"
              ></div>
              </div>
            )}
            
            {/* Info Overlay - only show when in crop mode */}
            {currentSettings.position === 'crop' && (
              <div className="crop-overlay">
                <div className="crop-info">
                  <span>Crop: {Math.round(cropBox.x)}%, {Math.round(cropBox.y)}%</span>
                  <span>Size: {Math.round(cropBox.width)}% × {Math.round(cropBox.height)}%</span>
                </div>
              </div>
            )}
          </div>
          <div className="preview-label">
            {currentSettings.position === 'crop' 
              ? '✅ Showing FULL image - drag green box to select crop area' 
              : 'Preview with positioning applied - click to enable crop mode'}
          </div>
        </div>

        {/* Simplified Comparison: Cropped Result vs Original */}
        <div className="preview-comparison">
          <div className="comparison-item">
            <div className="comparison-preview">
              <img 
                key={`cropped-result-${cropBox.x}-${cropBox.y}-${cropBox.width}-${cropBox.height}`}
                src={previewUrl || heroImage.url || heroImage.secure_url}
                alt="Cropped result"
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
              />
            </div>
            <div className="comparison-label">
              <i className="fas fa-check-circle"></i> Cropped Result
            </div>
          </div>
          <div className="comparison-item">
            <div className="comparison-preview">
              <img 
                key="original"
                src={heroImage.url || heroImage.secure_url}
                alt="Original"
                style={{ objectFit: 'contain', width: '100%', height: '100%' }}
              />
            </div>
            <div className="comparison-label">
              <i className="fas fa-image"></i> Original
            </div>
          </div>
        </div>
      </div>

      {/* Compact Crop Info */}
      {currentSettings.position === 'crop' && (
      <div className="crop-controls-compact" style={{ marginTop: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '6px' }}>
        <div className="crop-info-inline" style={{ display: 'flex', gap: '15px', fontSize: '0.85rem', color: '#666', marginBottom: '8px' }}>
          <span><strong>X:</strong> {Math.round(cropBox.x)}%</span>
          <span><strong>Y:</strong> {Math.round(cropBox.y)}%</span>
          <span><strong>W:</strong> {Math.round(cropBox.width)}%</span>
          <span><strong>H:</strong> {Math.round(cropBox.height)}%</span>
        </div>
        <div className="crop-tip-compact" style={{ fontSize: '0.8rem', color: '#888' }}>
          💡 Drag box to move • Use handles to resize • Smaller box = zoom in
        </div>
      </div>
      )}

      {/* Quick Position Modes */}
      <div className="crop-presets">
        <div className="preset-label">
          <i className="fas fa-magic"></i> Smart Positioning:
        </div>
        <div className="preset-buttons">
          <button
            type="button"
            className={`preset-btn ${currentSettings.position === 'face' ? 'active' : ''}`}
            onClick={() => {
              onSettingsChange({ 
                position: 'face',
                zoom: zoom,
                customX: 50,
                customY: 50
              });
            }}
            title="Uses AI to detect and focus on faces in the image"
          >
            <i className="fas fa-smile"></i> Face Detection (AI)
          </button>
          <button
            type="button"
            className={`preset-btn ${currentSettings.position === 'center' ? 'active' : ''}`}
            onClick={() => {
              onSettingsChange({ 
                position: 'center',
                zoom: zoom
              });
            }}
          >
            <i className="fas fa-crosshairs"></i> Center
          </button>
          <button
            type="button"
            className={`preset-btn ${currentSettings.position === 'top' ? 'active' : ''}`}
            onClick={() => {
              onSettingsChange({ 
                position: 'top',
                zoom: zoom
              });
            }}
          >
            <i className="fas fa-arrow-up"></i> Top
          </button>
          <button
            type="button"
            className={`preset-btn ${currentSettings.position === 'bottom' ? 'active' : ''}`}
            onClick={() => {
              onSettingsChange({ 
                position: 'bottom',
                zoom: zoom
              });
            }}
          >
            <i className="fas fa-arrow-down"></i> Bottom
          </button>
        </div>
      </div>

      {/* Quick Crop Presets - Only show in crop mode */}
      {currentSettings.position === 'crop' && (
      <div className="crop-presets">
        <div className="preset-label">
          <i className="fas fa-crop"></i> Crop Presets:
        </div>
        <div className="preset-buttons">
          <button
            type="button"
            className="preset-btn"
            onClick={() => {
              const newCropBox = { x: 25, y: 25, width: 50, height: 50 };
              setCropBox(newCropBox);
              onSettingsChange({ 
                position: 'crop',
                zoom: zoom,
                cropBox: newCropBox 
              });
            }}
          >
            <i className="fas fa-square"></i> Center Crop
          </button>
          <button
            type="button"
            className="preset-btn"
            onClick={() => {
              const newCropBox = { x: 0, y: 0, width: 60, height: 80 };
              setCropBox(newCropBox);
              onSettingsChange({ 
                position: 'crop',
                zoom: zoom,
                cropBox: newCropBox 
              });
            }}
          >
            <i className="fas fa-user"></i> Portrait
          </button>
          <button
            type="button"
            className="preset-btn"
            onClick={() => {
              const newCropBox = { x: 20, y: 10, width: 60, height: 40 };
              setCropBox(newCropBox);
              onSettingsChange({ 
                position: 'crop',
                zoom: zoom,
                cropBox: newCropBox 
              });
            }}
          >
            <i className="fas fa-image"></i> Landscape
          </button>
          <button
            type="button"
            className="preset-btn"
            onClick={() => {
              const newCropBox = { x: 10, y: 10, width: 80, height: 80 };
              setCropBox(newCropBox);
              onSettingsChange({ 
                position: 'crop',
                zoom: zoom,
                cropBox: newCropBox 
              });
            }}
          >
            <i className="fas fa-expand"></i> Wide Crop
          </button>
        </div>
      </div>
      )}

      {/* Reset and Mode Toggle Buttons */}
      <div className="hero-editor-actions">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={handleReset}
        >
          <i className="fas fa-undo"></i> Reset to Default (No Positioning)
        </button>
        <button
          type="button"
          className={`btn btn-sm ${currentSettings.position === 'crop' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => {
            if (currentSettings.position === 'crop') {
              onSettingsChange({ position: 'center', zoom: zoom });
            } else {
              onSettingsChange({ position: 'crop', zoom: zoom, cropBox: cropBox });
            }
          }}
        >
          <i className="fas fa-crop"></i> {currentSettings.position === 'crop' ? 'Crop Mode Active' : 'Enable Crop Mode'}
        </button>
      </div>
    </div>
  );
};

export default HeroImageEditor;

